// src/pages/GetHelp.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { HiSparkles } from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

function MathSymbol({ symbol, className, duration = 5 }) {
  return (
    <motion.div
      className={`absolute text-white/15 font-bold select-none pointer-events-none ${className}`}
      animate={{ y: [0, -14, 0], rotate: [0, 7, 0] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
    >
      {symbol}
    </motion.div>
  );
}

const FIELD_CLASS =
  "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white";

const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
    {children}
  </label>
);

export default function GetHelp() {
  const [form, setForm] = useState({
    name: "",
    whoFilling: "",
    board: "",
    class: "",
    subject: "",
    favSubject: "",
    lastScore: "",
    bestScore: "",
    phone: "",
    school: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = { createdAt: serverTimestamp() };
      if (form.name) data.name = form.name;
      if (form.whoFilling) data.whoFilling = form.whoFilling;
      if (form.board) data.board = form.board;
      if (form.class) data.class = form.class;
      if (form.subject) data.subject = form.subject;
      if (form.favSubject) data.favSubject = form.favSubject;
      if (form.lastScore) data.lastScore = Number(form.lastScore);
      if (form.bestScore) data.bestScore = Number(form.bestScore);
      if (form.phone) data.phone = form.phone;
      if (form.school) data.school = form.school;
      await addDoc(collection(db, "studentLeads"), data);
      setSubmitted(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero header ─────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white py-16 sm:py-24">
        <MathSymbol symbol="π"  className="text-6xl top-8  left-6"      duration={5.2} />
        <MathSymbol symbol="∑"  className="text-5xl top-14 right-12"    duration={4.7} />
        <MathSymbol symbol="√"  className="text-7xl bottom-6 left-16"   duration={6.1} />
        <MathSymbol symbol="∫"  className="text-5xl top-10 left-1/3"    duration={4.3} />
        <MathSymbol symbol="∞"  className="text-5xl bottom-4 right-20"  duration={5.8} />
        <MathSymbol symbol="x²" className="text-4xl top-20 right-1/4"   duration={3.9} />

        {/* Dot grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />

        <motion.div
          variants={stagger} initial="hidden" animate="visible"
          className="relative mx-auto max-w-2xl px-6 text-center"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/15 border border-white/30 backdrop-blur-sm text-white text-xs font-semibold tracking-widest uppercase px-5 py-2 rounded-full mb-6">
            <HiSparkles /> Free · No sign-up required
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            Get Personalised Help
          </motion.h1>
          <motion.p variants={fadeUp} className="text-blue-100 text-base sm:text-lg leading-relaxed">
            Tell us a little about yourself — our team will reach out with the right guidance just for you.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Form ─────────────────────────────────────────── */}
      <section className="py-14 px-4">
        <div className="mx-auto max-w-2xl">

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 p-12 text-center"
            >
              <div className="text-6xl mb-5">✅</div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">We've got your details!</h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
                Our team will reach out to you soon with personalised guidance. You can close this tab.
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8"
            >
              <p className="text-xs text-gray-400 mb-6 text-center">
                All fields are optional — fill in as much or as little as you like.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                {/* Name — full width */}
                <div className="sm:col-span-2">
                  <Label>Your Name</Label>
                  <input
                    type="text"
                    placeholder="e.g. Arun Kumar"
                    value={form.name}
                    onChange={set("name")}
                    className={FIELD_CLASS}
                  />
                </div>

                {/* Who's filling — full width */}
                <div className="sm:col-span-2">
                  <Label>I am a</Label>
                  <select
                    value={form.whoFilling}
                    onChange={set("whoFilling")}
                    className={FIELD_CLASS}
                  >
                    <option value="">Select one</option>
                    <option>Student</option>
                    <option>Parent</option>
                    <option>Teacher</option>
                    <option>Academy Owner</option>
                  </select>
                </div>

                <div>
                  <Label>Board</Label>
                  <select value={form.board} onChange={set("board")} className={FIELD_CLASS}>
                    <option value="">Select board</option>
                    <option>State Board</option>
                    <option>CBSE</option>
                    <option>Competitive</option>
                  </select>
                </div>

                <div>
                  <Label>Class</Label>
                  <select value={form.class} onChange={set("class")} className={FIELD_CLASS}>
                    <option value="">Select class</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1}>Class {i + 1}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Subject you find hard</Label>
                  <select value={form.subject} onChange={set("subject")} className={FIELD_CLASS}>
                    <option value="">Select subject</option>
                    <option>Maths</option>
                    <option>Science</option>
                    <option>English</option>
                    <option>Tamil</option>
                    <option>Social</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <Label>Favourite Subject</Label>
                  <input
                    type="text"
                    placeholder="e.g. Maths"
                    value={form.favSubject}
                    onChange={set("favSubject")}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <Label>Last Scored Marks (%)</Label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="e.g. 72"
                    value={form.lastScore}
                    onChange={set("lastScore")}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <Label>Best Marks Ever (%)</Label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="e.g. 88"
                    value={form.bestScore}
                    onChange={set("bestScore")}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <Label>Phone Number</Label>
                  <input
                    type="tel"
                    placeholder="e.g. +91 98765 43210"
                    value={form.phone}
                    onChange={set("phone")}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <Label>School / Institution Name</Label>
                  <input
                    type="text"
                    placeholder="e.g. Govt. Hr. Sec. School"
                    value={form.school}
                    onChange={set("school")}
                    className={FIELD_CLASS}
                  />
                </div>

              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-3.5 rounded-xl transition-colors duration-200 text-sm tracking-wide"
              >
                {submitting ? "Submitting..." : "Submit — Get Personalised Help →"}
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                Your information is only used to reach out to you. We don't share it with anyone.
              </p>
            </motion.div>
          )}
        </div>
      </section>

    </div>
  );
}
